import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Image,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AGENDA_ITEM_BODY_MAX,
  AGENDA_TITLE_MAX,
  type AgendaItem,
  type AgendaKind,
  type CustomerSafeAttachment,
  type CustomerSafeRichComment,
  type CustomerSafeTicket,
  type TicketSummaryDto,
} from "@msc/shared";

import { isJiraReauthRequired, useCustomerBranding, useTicketDetail } from "../../hooks/useCustomerView.js";
import { useSetDueDate } from "../../hooks/useCustomerViewActions.js";
import { useEditSummary } from "../../hooks/usePresentation.js";
import { renderAgendaMarkdown } from "../../lib/customerView/agendaMarkdown.js";
import { nextUp, type NextUp } from "../../lib/customerView/agendaPrep.js";
import { agendaTitleFallback, agendaTitleOf } from "../../lib/customerView/agendaTitle.js";
import { attachmentUrl, referencedAttachmentIds } from "../../lib/customerView/attachments.js";
import { hasCustomerDesign, resolveStageTheme } from "../../lib/customerView/brandingTheme.js";
import { sortNewestFirst } from "../../lib/customerView/commentOrder.js";
import { groupHeaders } from "../../lib/customerView/deckGroups.js";
import { DECK_MAX } from "../../lib/customerView/deckLimit.js";
import { mergeKnownTickets } from "../../lib/customerView/knownTickets.js";
import { openPresentChannel, type PresentChannel } from "../../lib/customerView/presentChannel.js";
import { CELEBRATE_DELAY_MS, isTypingTarget, navActionForKey, planAdvance } from "../../lib/customerView/presenterKeys.js";
import { buildStageSnapshot } from "../../lib/customerView/stageSnapshot.js";
import { groupLabelOf } from "../../lib/customerView/ticketView.js";
import { isStageWindowOpen, openStageWindow } from "../../lib/customerView/stageWindow.js";
import { absoluteDate } from "../../lib/customerView/relativeTime.js";
import { formatBerlinDate, fromLocalDateString, toLocalDateString } from "../../lib/date.js";
import { nextStageTheme, usePresentationStore } from "../../stores/presentation.js";
import { isNewSinceLastMeeting, useReviewedStore } from "../../stores/reviewed.js";
import { StageSlides } from "../stage/StageSlides.js";
import { ChecklistPanel } from "./ChecklistPanel.js";
import { CommentComposer } from "./CommentComposer.js";
import { NewTicketModal } from "./NewTicketModal.js";
import { ProtocolModal } from "./ProtocolModal.js";
import { ReconnectBanner } from "./ReconnectBanner.js";
import { RichHtml } from "./RichHtml.js";
import { TransitionMenu } from "./TransitionMenu.js";

import "../stage/stage.css";

const SUMMARY_MAX_LENGTH = 600;

export interface PresenterCockpitProps {
  tickets: CustomerSafeTicket[];
  customerObjectKey: string;
  customerName: string;
}

/**
 * Die Referentenansicht des Leads — der EIGENTÜMER des Präsentationszustands.
 * Drei Spalten: Agenda (wo stehen wir), Vorschau (was sieht der Kunde) und
 * „Nur für dich" (was er nicht sieht: interne Kommentare, Notizen, die
 * Redaktion der Zusammenfassung).
 *
 * Über den Bühnenkanal geht ausschließlich das Ergebnis von
 * `buildStageSnapshot` (Sicherheitsinvariante): Notizen, Fallback-Kennzeichen
 * und nicht freigegebene interne Kommentare erreichen die Bühne nie. Die
 * Bühne selbst ist ein dummer Renderer — sie fragt nichts nach, deshalb
 * schickt das Cockpit nach JEDER Änderung den vollständigen Snapshot und
 * beantwortet `state-request` (Reload/Neuöffnen des Kundenfensters).
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/pages/PresenterCockpit.tsx`
 * samt `components/cockpit/panels.tsx`) auf Mantine; Abweichungen: keine
 * Tailwind-Klassen, Zeichenketten über `t()`, Statuswechsel über den
 * bestehenden `TransitionMenu` (Scheibe 3) und der Erledigt-Moment als
 * ausdrückliche Zwei-Schritt-Folge (s. `handleNext`).
 */
export function PresenterCockpit({ tickets, customerObjectKey, customerName }: PresenterCockpitProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  // Ab `md` bekommt das Cockpit eine feste Höhe, damit die Steuerleiste unten
  // nicht mehr je nach Folie springt (Begründung s. unten am Stack). Darunter
  // sind die drei Spalten gestapelt (Grid.Col span base=12) und behalten die
  // bisherige, fließend hohe Darstellung — ein `overflow: hidden`-Grid würde
  // die gestapelten Spalten sonst auf den für nebeneinander berechneten
  // Platz zusammenstauchen. Gleiches Breakpoint-Muster wie in AppLayout.tsx.
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`) ?? true;

  const deck = usePresentationStore((s) => s.deck);
  const mode = usePresentationStore((s) => s.mode);
  const currentIndex = usePresentationStore((s) => s.currentIndex);
  const summaries = usePresentationStore((s) => s.summaries);
  const events = usePresentationStore((s) => s.events);
  const stageTheme = usePresentationStore((s) => s.stageTheme);
  const releasedInternal = usePresentationStore((s) => s.releasedInternal);
  const autoReview = usePresentationStore((s) => s.autoReview);
  const notes = usePresentationStore((s) => s.notes);
  const shownAttachment = usePresentationStore((s) => s.shownAttachment);
  const focusedComment = usePresentationStore((s) => s.focusedComment);
  const deckGroups = usePresentationStore((s) => s.deckGroups);
  const deckGroupBy = usePresentationStore((s) => s.deckGroupBy);
  const agendaItems = usePresentationStore((s) => s.agendaItems);
  const agendaIndex = usePresentationStore((s) => s.agendaIndex);
  const goToAgendaItem = usePresentationStore((s) => s.goToAgendaItem);
  const updateAgendaItem = usePresentationStore((s) => s.updateAgendaItem);
  const goTo = usePresentationStore((s) => s.goTo);
  const prev = usePresentationStore((s) => s.prev);
  const pause = usePresentationStore((s) => s.pause);
  const resume = usePresentationStore((s) => s.resume);
  const endPresentation = usePresentationStore((s) => s.end);
  const setSummary = usePresentationStore((s) => s.setSummary);
  const cycleStageTheme = usePresentationStore((s) => s.cycleStageTheme);
  const toggleAutoReview = usePresentationStore((s) => s.toggleAutoReview);
  const toggleInternalRelease = usePresentationStore((s) => s.toggleInternalRelease);
  const setNote = usePresentationStore((s) => s.setNote);
  const showAttachment = usePresentationStore((s) => s.showAttachment);
  const focusComment = usePresentationStore((s) => s.focusComment);
  const addToDeck = usePresentationStore((s) => s.addToDeck);
  const ownerId = usePresentationStore((s) => s.ownerId);

  const reviewedAt = useReviewedStore((s) => s.reviewedAt);
  const toggleReviewed = useReviewedStore((s) => s.toggle);
  const reviewedKeys = useMemo(() => new Set(Object.keys(reviewedAt)), [reviewedAt]);

  // Alles, was während DIESER Präsentation je in der Liste stand — nur ergänzt
  // und aktualisiert, nie entfernt (`mergeKnownTickets`). Die Ticketabfrage
  // führt neben den offenen Vorgängen nur die kürzlich „Erledigt"-en; wer im
  // Termin auf einen anderen Done-Status wechselt (etwa „Geschlossen"), fällt
  // beim verzögerten Neuladen nach `JIRA_INDEX_LAG_MS` aus der Liste. Der
  // Kunde muss die Folie trotzdem weiter sehen — sie ist gerade Thema.
  const knownRef = useRef(new Map<string, CustomerSafeTicket>());
  const byKey = useMemo(() => {
    knownRef.current = mergeKnownTickets(knownRef.current, tickets);
    return knownRef.current;
  }, [tickets]);
  // Was auf die Bühne geht, kommt aus derselben Map — in Deckreihenfolge.
  const stageTickets = useMemo(
    () => deck.map((k) => byKey.get(k)).filter((x): x is CustomerSafeTicket => x !== undefined),
    [deck, byKey],
  );
  const currentKey = mode === "ticket" ? deck[currentIndex] ?? null : null;
  const currentTicket = currentKey ? byKey.get(currentKey) ?? null : null;
  /** Die AKTIVIERTEN Punkte — dieselbe Zählung wie Snapshot, Store und Bühne. */
  const enabledAgenda = useMemo(() => agendaItems.filter((a) => a.enabled), [agendaItems]);
  const currentAgendaItem = mode === "agendaItem" ? enabledAgenda[agendaIndex] ?? null : null;

  /**
   * „Als Nächstes" muss dieselben Übergänge kennen wie `next()` im Store —
   * seit D9 kann das auch ein Agenda-Punkt sein. Die Entscheidung steht als
   * reine Funktion daneben (`nextUp`), damit sie prüfbar bleibt und nicht zur
   * zweiten, leise abweichenden Ableitung wird.
   */
  const upNext = useMemo<NextUp>(
    () => nextUp({ mode, agendaItems, agendaIndex, deck, currentIndex }),
    [mode, agendaItems, agendaIndex, deck, currentIndex],
  );
  const upNextView = useMemo(() => {
    if (upNext === null) return null;
    if (upNext.kind === "agendaItem") {
      const item = enabledAgenda[upNext.index];
      return { badge: null, title: item ? agendaTitleOf(item, t) : upNext.title };
    }
    return { badge: upNext.key, title: byKey.get(upNext.key)?.title ?? upNext.key };
  }, [upNext, byKey, enabledAgenda, t]);

  /**
   * Kundendesign (D10). Fail-soft: Solange es lädt oder wenn es fehlt, läuft
   * die Bühne ohne — `customer` bleibt dann im Theme-Wechsel ausgespart, und
   * ein schon gesetztes `customer` zeichnet die Bühne als „dunkel".
   */
  const branding = useCustomerBranding(customerObjectKey).data ?? null;
  const hasDesign = hasCustomerDesign(branding);
  const nextTheme = nextStageTheme(stageTheme, hasDesign);

  // Das Detail liefert Fälligkeit und die reichen Kommentare (inkl. interner)
  // — nur fürs Cockpit; auf die Bühne kommt davon nur, was freigegeben ist.
  const detail = useTicketDetail(currentKey);
  /**
   * ALLE Kommentare des Tickets, neueste zuerst — nicht nur die internen.
   * Im Termin ist der Verlauf die Erzählung („zuletzt haben wir …"), und der
   * letzte Eintrag ist der interessante; er stand vorher ganz unten. Die
   * Freigabe (Schalter) gibt es weiterhin nur für interne Kommentare — ein
   * kundensichtbarer ist bereits auf der Bühne.
   *
   * Die Reihenfolge auf der BÜHNE bleibt davon unberührt: Sie kommt aus
   * `buildStageSnapshot` und ist dort bewusst die Reihenfolge der Quelle.
   *
   * Sortierung über `sortNewestFirst` (Zeitpunkt, nicht Zeichenkette): Jira
   * liefert `createdIso` mit Zonen-Offset (`+0200`), der optimistische
   * Eintrag mit `Z` — als Zeichenkette verglichen stünde `+0200` vor `Z`,
   * unabhängig vom tatsächlichen Zeitpunkt.
   */
  const comments = useMemo(
    () => sortNewestFirst(detail.data?.comments ?? []),
    [detail.data],
  );

  /**
   * Für die BÜHNE tragen leere Titel ihren Vorgabetitel — der Lead darf das
   * Feld ausräumen, der Kunde soll trotzdem keine leere Überschrift sehen.
   * Die Bearbeitung unten bekommt bewusst die ROHEN Punkte: Sonst spränge der
   * Vorgabetitel in das Eingabefeld zurück, sobald jemand es leert.
   */
  const agendaItemsForStage = useMemo(
    () => agendaItems.map((a) => ({ ...a, title: agendaTitleOf(a, t) })),
    [agendaItems, t],
  );

  const snapshot = useMemo(
    () =>
      buildStageSnapshot({
        mode,
        theme: stageTheme,
        deck,
        currentIndex,
        summaries,
        events,
        tickets: stageTickets,
        customerName,
        reviewedKeys,
        currentDueDate: detail.data?.dueDate ?? null,
        currentDetailComments: detail.data?.comments ?? null,
        currentDescriptionHtml: detail.data?.descriptionHtml ?? null,
        currentReleasedInternal: currentKey ? releasedInternal[currentKey] ?? [] : [],
        shownAttachment,
        focusedComment,
        deckGroups,
        agendaItems: agendaItemsForStage,
        agendaIndex,
        branding,
      }),
    [mode, stageTheme, deck, currentIndex, summaries, events, stageTickets, customerName, reviewedKeys, detail.data, currentKey, releasedInternal, shownAttachment, focusedComment, deckGroups, agendaItemsForStage, agendaIndex, branding],
  );
  /** Dieselbe Entscheidung wie auf der Bühne (`StageFrame`) — die Vorschau darf nie etwas anderes zeigen. */
  const previewTheme = resolveStageTheme(snapshot);

  // ── Bühnenkanal ───────────────────────────────────────────────────────────
  const [stageConnected, setStageConnected] = useState(() => isStageWindowOpen());
  const channelRef = useRef<PresentChannel | null>(null);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  /**
   * Weiter/Zurück aus dem Kundenfenster (#267). Über einen Ref, damit der
   * Kanal-Effekt nicht bei jeder neuen `handleNext`-Identität einen neuen
   * BroadcastChannel öffnet; gesetzt weiter unten, sobald beide existieren.
   */
  const navRef = useRef<{ next: () => void; prev: () => void } | null>(null);

  useEffect(() => {
    const channel = openPresentChannel(ownerId, (message) => {
      if (message.type === "state-request") {
        channel.post({ type: "state", snapshot: snapshotRef.current });
        setStageConnected(true);
      } else if (message.type === "ready") setStageConnected(true);
      // `closed` ist KEIN Ende der Präsentation: Es kommt auch beim Neuladen
      // des Bühnenfensters und beim Doppel-Mount im StrictMode. Es färbt nur
      // die Anzeige — beendet wird ausschließlich per Klick.
      else if (message.type === "closed") setStageConnected(false);
      // Dieselben Wege wie Tastatur und Knöpfe im Cockpit — „Weiter" also
      // samt Auto-Haken, nicht ein nacktes `next()`.
      else if (message.type === "nav") {
        if (message.action === "next") navRef.current?.next();
        else navRef.current?.prev();
      }
    });
    channelRef.current = channel;
    channel.post({ type: "state", snapshot: snapshotRef.current });
    return () => {
      channelRef.current = null;
      channel.close();
    };
  }, [ownerId]);

  useEffect(() => {
    channelRef.current?.post({ type: "state", snapshot });
  }, [snapshot]);

  const reopenStage = () => {
    if (openStageWindow(ownerId) === null) {
      notifications.show({ color: "yellow", message: t("pages.customerView.present.stageBlocked") });
    }
  };

  // ── Weiterblättern mit Auto-Haken ─────────────────────────────────────────
  // Zwei Schritte statt einem: erst abhaken (die Bühne feiert den Haken, solange
  // das Ticket noch die aktuelle Folie ist), dann umblättern.
  const advanceTimeoutRef = useRef<number | null>(null);
  const flushPendingAdvance = useCallback(() => {
    if (advanceTimeoutRef.current === null) return;
    window.clearTimeout(advanceTimeoutRef.current);
    advanceTimeoutRef.current = null;
    usePresentationStore.getState().next();
  }, []);

  const handleNext = useCallback(() => {
    // Zwei schnelle „Weiter" hintereinander: das ausstehende Umblättern sofort
    // nachziehen, statt es zu verschlucken.
    flushPendingAdvance();
    const state = usePresentationStore.getState();
    const plan = planAdvance({
      autoReview: state.autoReview,
      mode: state.mode,
      deck: state.deck,
      currentIndex: state.currentIndex,
      reviewedKeys: new Set(Object.keys(useReviewedStore.getState().reviewedAt)),
    });
    if (plan.markKey === null) {
      state.next();
      return;
    }
    useReviewedStore.getState().markReviewed(plan.markKey);
    advanceTimeoutRef.current = window.setTimeout(() => {
      advanceTimeoutRef.current = null;
      usePresentationStore.getState().next();
    }, CELEBRATE_DELAY_MS);
  }, [flushPendingAdvance]);

  useEffect(
    () => () => {
      if (advanceTimeoutRef.current !== null) window.clearTimeout(advanceTimeoutRef.current);
    },
    [],
  );

  navRef.current = { next: handleNext, prev };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const action = navActionForKey(event);
      if (action === null) return;
      if (isTypingTarget(event.target)) return;
      // Leertaste auf einem fokussierten Button (z. B. eine Agenda-Zeile,
      // `UnstyledButton`) aktiviert IHN — sonst würde `preventDefault` den
      // nativen Klick unterdrücken und gleichzeitig das Cockpit umblättern.
      const isButtonTarget = event.target instanceof HTMLElement && event.target.tagName === "BUTTON";
      if (event.key === " " && isButtonTarget) return;
      event.preventDefault();
      if (action === "next") handleNext();
      else prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleNext, prev]);

  // ── Abschluss ─────────────────────────────────────────────────────────────
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const protocolAutoOpened = useRef(false);
  useEffect(() => {
    if (mode !== "end" || protocolAutoOpened.current) return;
    protocolAutoOpened.current = true;
    setProtocolOpen(true);
  }, [mode]);

  const setDueDate = useSetDueDate(customerObjectKey);
  const notInDeck = useMemo(() => tickets.filter((x) => !deck.includes(x.key)), [tickets, deck]);

  const phaseLabel =
    mode === "ticket"
      ? t("pages.customerView.present.phaseTicket", { n: currentIndex + 1, total: deck.length })
      : mode === "agendaItem"
        ? t("pages.customerView.present.phaseAgendaItem", {
            n: agendaIndex + 1,
            total: enabledAgenda.length,
          })
        : mode === "pause"
          ? t("pages.customerView.present.phasePause")
          : mode === "end"
            ? t("pages.customerView.present.phaseEnd")
            : t("pages.customerView.present.phaseAgenda");

  return (
    <Stack
      gap="sm"
      style={
        isDesktop
          ? {
              // Feste Höhe statt `flex: 1`: Das Grid darunter bekommt seine
              // Höhe darüber garantiert per `flex: 1` zugewiesen, statt sich
              // (wie zuvor) am Inhalt der jeweils höchsten Spalte auszurichten
              // — genau das ließ die Steuerleiste unten je nach Folie
              // springen. Die Spalten scrollen stattdessen intern.
              //
              // 320px ist derselbe Versatz, den die Route für ihre eigenen
              // ScrollAreas an GENAU dieser Stelle im Layout verwendet
              // (customer-view.tsx, `calc(100vh - 320px)` für Ticketliste und
              // -detail) — das Cockpit ersetzt jene Zeile 1:1 im selben
              // `<Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>` der
              // Route. Der Wert lässt sich unverändert übernehmen: Dem
              // Cockpit fehlt zwar die dortige Symbolleiste (TicketToolbar)
              // und die Paper-Innenabstände der Detailspalte, dafür kommen
              // hier zusätzlich die Statuszeile oben und die Steuerleiste
              // unten hinzu — beides gleicht sich in der Praxis etwa aus.
              height: "calc(100vh - 320px)",
              minHeight: 0,
            }
          : { flex: 1, minHeight: 0 }
      }
    >
      <Paper withBorder p="xs" style={{ flexShrink: 0 }}>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs">
            <Badge color="red" variant="filled" size="sm">{t("pages.customerView.present.live")}</Badge>
            <Text size="sm" fw={600}>{t("pages.customerView.present.running", { customer: customerName })}</Text>
            <Text size="sm" c="dimmed">{phaseLabel}</Text>
          </Group>
          <Group gap="sm" wrap="wrap">
            <Switch
              size="xs"
              checked={autoReview}
              onChange={toggleAutoReview}
              label={t("pages.customerView.present.autoReview")}
              description={t("pages.customerView.present.autoReviewHint")}
            />
            {/* Der Knopf nennt, wohin er schaltet — nicht, was gerade läuft. */}
            <Button size="compact-xs" variant="default" onClick={() => cycleStageTheme(hasDesign)}>
              {nextTheme === "light"
                ? t("pages.customerView.present.stageThemeLight")
                : nextTheme === "customer"
                  ? t("pages.customerView.present.stageThemeCustomer")
                  : t("pages.customerView.present.stageThemeDark")}
            </Button>
            <Badge size="sm" variant="light" color={stageConnected ? "green" : "red"}>
              {stageConnected
                ? t("pages.customerView.present.stageOpen")
                : t("pages.customerView.present.stageClosed")}
            </Badge>
            {!stageConnected && (
              <Button size="compact-xs" variant="light" onClick={reopenStage}>
                {t("pages.customerView.present.stageReopen")}
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      <Grid
        gutter="sm"
        style={isDesktop ? { flex: 1, minHeight: 0, overflow: "hidden" } : { flex: 1, minHeight: 0 }}
        // Mantines Grid streckt Spalten per Default zwar schon über
        // `align-items: stretch` auf die Höhe der `.inner`-Zeile — die Zeile
        // selbst bekommt aber nie eine explizite Höhe zugewiesen (die
        // `Grid.css` setzt dafür keinen Wert), sie bleibt inhaltsgetrieben.
        // Ohne `inner: { height: "100%" }` würde das äußere `flex: 1` also
        // nur die Grid-Wurzel füllen, nicht die Spalten darin.
        styles={isDesktop ? { inner: { height: "100%" } } : undefined}
      >
        <Grid.Col
          span={{ base: 12, md: 3 }}
          style={isDesktop ? { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" } : undefined}
        >
          <AgendaColumn
            deck={deck}
            byKey={byKey}
            agendaItems={enabledAgenda}
            agendaIndex={mode === "agendaItem" ? agendaIndex : -1}
            onGoToAgendaItem={goToAgendaItem}
            currentIndex={mode === "ticket" ? currentIndex : -1}
            reviewedKeys={reviewedKeys}
            reviewedAt={reviewedAt}
            onToggleReviewed={toggleReviewed}
            onGoTo={goTo}
            notInDeck={notInDeck}
            // Ein Nachzügler bekommt seine Gruppe JETZT — nach derselben Regel
            // wie die Liste beim Start, eingefroren wie alle anderen.
            onAddToDeck={(k) => {
              const ticket = byKey.get(k);
              addToDeck(k, ticket ? groupLabelOf(ticket, deckGroupBy) : null);
            }}
            deckGroups={deckGroups}
            isDesktop={isDesktop}
          />
        </Grid.Col>

        <Grid.Col
          span={{ base: 12, md: 5 }}
          style={isDesktop ? { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" } : undefined}
        >
          <Stack gap={6} h="100%">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              {t("pages.customerView.present.preview")}
            </Text>
            {/*
              Die Bühnenfolien im Original, nur halb so groß: Der äußere Kasten
              gibt 16:9 vor, der innere ist doppelt so breit und wird auf die
              Hälfte skaliert. `.stage-preview` statt `.stage` — Letzteres ist
              `position: fixed` und legte sich über das ganze Cockpit.
            */}
            <Paper withBorder style={{ overflow: "hidden" }}>
              <Box style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", overflow: "hidden" }}>
                <Box style={{ position: "absolute", top: 0, left: 0, width: "200%", transform: "scale(.5)", transformOrigin: "top left" }}>
                  <div className="stage-preview" data-stage-theme={previewTheme.theme} style={previewTheme.style}>
                    <StageSlides snapshot={snapshot} />
                  </div>
                </Box>
              </Box>
            </Paper>
          </Stack>
        </Grid.Col>

        <Grid.Col
          span={{ base: 12, md: 4 }}
          style={isDesktop ? { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" } : undefined}
        >
          <PrivateColumn
            key={currentKey ?? "none"}
            currentTicket={currentTicket}
            customerObjectKey={customerObjectKey}
            agendaItem={currentAgendaItem}
            onAgendaChange={updateAgendaItem}
            upNext={upNextView}
            detailError={detail.isError ? detail.error : null}
            summary={currentKey ? summaries[currentKey] ?? null : null}
            onSummarySaved={setSummary}
            comments={comments}
            releasedInternal={currentKey ? releasedInternal[currentKey] ?? [] : []}
            onToggleRelease={(commentId) => currentKey && toggleInternalRelease(currentKey, commentId)}
            note={currentKey ? notes[currentKey] ?? "" : ""}
            onNoteChange={(text) => currentKey && setNote(currentKey, text)}
            shownAttachmentId={
              shownAttachment && shownAttachment.ticketKey === currentKey ? shownAttachment.id : null
            }
            onShowAttachment={(a) =>
              currentKey && showAttachment(a ? { ticketKey: currentKey, ...a } : null)
            }
            focusedCommentId={
              focusedComment && focusedComment.ticketKey === currentKey ? focusedComment.id : null
            }
            onFocusComment={(id) => currentKey && focusComment(id ? { ticketKey: currentKey, id } : null)}
            isDesktop={isDesktop}
          />
        </Grid.Col>
      </Grid>

      <Paper
        withBorder
        p="xs"
        style={
          isDesktop
            ? { flexShrink: 0 }
            : // Mobil bleibt die Steuerleiste beim Scrollen der gestapelten
              // Spalten erreichbar, statt aus dem Bildschirm zu laufen; das
              // Paper hat bereits einen deckenden Hintergrund (siehe
              // `Paper.css`, `background-color: var(--mantine-color-body)`).
              { flexShrink: 0, position: "sticky", bottom: 0, zIndex: 2 }
        }
      >
        <Group gap="sm" wrap="wrap">
          <Button variant="default" onClick={prev}>{t("pages.customerView.present.back")}</Button>
          <Button onClick={handleNext}>{t("pages.customerView.present.forward")}</Button>
          {mode === "pause" ? (
            <Button variant="light" color="yellow" onClick={resume}>{t("pages.customerView.present.resume")}</Button>
          ) : (
            <Button variant="default" onClick={pause}>{t("pages.customerView.present.pause")}</Button>
          )}

          {currentTicket && (
            <>
              <Divider orientation="vertical" />
              <TransitionMenu
                ticketKey={currentTicket.key}
                objectKey={customerObjectKey}
                currentStatus={currentTicket.status}
                currentDueDate={detail.data?.dueDate ?? null}
              />
              {/* Absprung in die Agentenansicht (#273). `browseUrl` gehört
                  zum Ticket-Detail des Leads und steht in keinem Bühnen-DTO —
                  der Link erreicht den Kunden nicht. */}
              {detail.data?.browseUrl && (
                <Button
                  size="xs"
                  variant="default"
                  component="a"
                  href={detail.data.browseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("pages.customerView.cockpit.openInJira")}
                </Button>
              )}
              <DateInput
                size="xs"
                w={150}
                clearable
                aria-label={t("pages.customerView.cockpit.actions.dueDate")}
                placeholder={t("pages.customerView.cockpit.actions.dueDate")}
                clearButtonProps={{ "aria-label": t("pages.customerView.cockpit.actions.dueDateClear") }}
                value={detail.data?.dueDate ? fromLocalDateString(detail.data.dueDate) : null}
                disabled={setDueDate.isPending}
                onChange={(d) =>
                  setDueDate.mutate({ key: currentTicket.key, dueDate: d ? toLocalDateString(d) : null })
                }
              />
            </>
          )}

          <Group gap="sm" ml="auto">
            <Button variant="default" onClick={() => setNewTicketOpen(true)}>
              {t("pages.customerView.newTicket.open")}
            </Button>
            {mode === "end" && (
              <Button variant="light" onClick={() => setProtocolOpen(true)}>
                {t("pages.customerView.present.protocolOpen")}
              </Button>
            )}
            <Button variant="outline" color="red" onClick={endPresentation}>
              {t("pages.customerView.present.end")}
            </Button>
          </Group>
        </Group>
      </Paper>

      <NewTicketModal
        opened={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        objectKey={customerObjectKey}
        customerName={customerName}
        // Ins Deck sofort: Die Folie erscheint, sobald Jiras Suche den
        // Vorgang liefert (s. useCreateTicket).
        onCreated={(key, customerDepartmentSet) => {
          addToDeck(key, null);
          notifications.show({
            color: customerDepartmentSet ? "green" : "yellow",
            title: t("pages.customerView.newTicket.created", { key }),
            message: t(customerDepartmentSet ? "pages.customerView.newTicket.createdInDeck" : "pages.customerView.newTicket.noDepartment"),
            autoClose: customerDepartmentSet ? 5000 : false,
          });
        }}
      />

      {mode === "end" && (
        <ProtocolModal
          opened={protocolOpen}
          onClose={() => setProtocolOpen(false)}
          customerObjectKey={customerObjectKey}
          customerName={customerName}
        />
      )}
    </Stack>
  );
}

/** Agenda: Agenda-Punkte, Reihenfolge, Haken, „Neu"-Marker — und der Nachzügler ins Deck. */
function AgendaColumn({
  deck,
  byKey,
  agendaItems,
  agendaIndex,
  onGoToAgendaItem,
  currentIndex,
  reviewedKeys,
  reviewedAt,
  onToggleReviewed,
  onGoTo,
  notInDeck,
  onAddToDeck,
  deckGroups,
  isDesktop,
}: {
  deck: string[];
  byKey: Map<string, CustomerSafeTicket>;
  /** Nur die AKTIVIERTEN Punkte, in derselben Zählung wie `agendaIndex`. */
  agendaItems: AgendaItem[];
  agendaIndex: number;
  onGoToAgendaItem: (i: number) => void;
  currentIndex: number;
  reviewedKeys: Set<string>;
  reviewedAt: Record<string, string>;
  onToggleReviewed: (key: string) => void;
  onGoTo: (index: number) => void;

  notInDeck: CustomerSafeTicket[];
  onAddToDeck: (key: string) => void;
  /** Eingefrorene Gruppe je Ticket (#278). */
  deckGroups: Record<string, string>;
  isDesktop: boolean;
}) {
  const { t } = useTranslation();
  const headers = useMemo(() => groupHeaders(deck, (k) => deckGroups[k]), [deck, deckGroups]);
  return (
    <Paper withBorder p="xs" h="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
        {t("pages.customerView.present.agenda")}
      </Text>
      {/* Am `md`-Breakpoint füllt die Spalte per `flex: 1` die vom Grid
          zugewiesene (feste) Höhe; darunter ist die feste Höhe der 420px-
          Herleitung mangels definierter Elternhöhe die verlässlichere Wahl
          (s. Begründung am `isDesktop` weiter oben). */}
      <ScrollArea h={isDesktop ? undefined : "calc(100vh - 420px)"} style={{ flex: 1, minHeight: 0 }}>
        <Stack gap={2}>
          {/* Die Agenda-Punkte laufen im Termin VOR den Tickets — also stehen
              sie auch hier vorn, mit eigener Zählung (der Fortschritt auf der
              Bühne sagt „Agenda i von n"). */}
          {agendaItems.map((a, i) => (
            <Group
              key={a.kind}
              gap="xs"
              wrap="nowrap"
              px={6}
              py={4}
              style={{
                borderLeft: `3px solid ${i === agendaIndex ? "var(--mantine-color-yellow-6)" : "transparent"}`,
                background: i === agendaIndex ? "var(--mantine-color-yellow-light)" : undefined,
              }}
            >
              <UnstyledButton onClick={() => onGoToAgendaItem(i)} style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6} wrap="nowrap">
                  <Text size="xs" c="dimmed" ff="monospace">{i + 1}.</Text>
                  {/* Leerer Titel? Dann der Vorgabetitel der Art — dieselbe
                      Regel wie auf der Bühne und im Protokoll. */}
                  <Text size="sm" lineClamp={1}>{agendaTitleOf(a, t)}</Text>
                </Group>
              </UnstyledButton>
            </Group>
          ))}
          {agendaItems.length > 0 && <Divider my={4} />}
          {deck.map((key, i) => {
            const ticket = byKey.get(key) ?? null;
            const done = reviewedKeys.has(key);
            const isNew = ticket ? isNewSinceLastMeeting(ticket, reviewedAt) : false;
            const header = headers.get(i);
            return (
              <Fragment key={key}>
              {header !== undefined && (
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" px={6} pt={i === 0 ? 0 : 6}>
                  {header}
                </Text>
              )}
              <Group
                gap="xs"
                wrap="nowrap"
                px={6}
                py={4}
                style={{
                  borderLeft: `3px solid ${i === currentIndex ? "var(--mantine-color-yellow-6)" : "transparent"}`,
                  background: i === currentIndex ? "var(--mantine-color-yellow-light)" : undefined,
                }}
              >
                <Checkbox
                  size="xs"
                  checked={done}
                  onChange={() => onToggleReviewed(key)}
                  aria-label={`${t("pages.customerView.present.reviewedToggle")}: ${key}`}
                />
                <UnstyledButton onClick={() => onGoTo(i)} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="xs" c="dimmed" ff="monospace">{i + 1}.</Text>
                    {isNew && <Badge size="xs" variant="light" color="yellow">{t("pages.customerView.present.new")}</Badge>}
                    <Text size="sm" lineClamp={1} td={done ? "line-through" : undefined} c={done ? "dimmed" : undefined}>
                      {ticket?.title ?? key}
                    </Text>
                  </Group>
                </UnstyledButton>
              </Group>
              </Fragment>
            );
          })}
        </Stack>
      </ScrollArea>
      {notInDeck.length > 0 && (
        <Select
          mt="xs"
          size="xs"
          searchable
          value={null}
          placeholder={t("pages.customerView.present.addTicket")}
          aria-label={t("pages.customerView.present.addTicket")}
          data={notInDeck.map((x) => ({ value: x.key, label: `${x.key} — ${x.title}` }))}
          onChange={(v) => { if (v) onAddToDeck(v); }}
          disabled={deck.length >= DECK_MAX}
          description={deck.length >= DECK_MAX ? t("pages.customerView.present.deckFull") : undefined}
        />
      )}
    </Paper>
  );
}

/**
 * „Nur für dich": alles, was der Kunde NICHT sieht — plus die Freigaben.
 *
 * Die Reihenfolge der Abschnitte ist eine Entscheidung, keine Gewohnheit:
 * Zusammenfassung, Kommentarverlauf, Notizen, übrige Bilder — und GANZ unten,
 * klebend, das Kommentarfeld. Es ist das Einzige, das im Termin jederzeit
 * erreichbar sein muss; alles andere liest man, wenn man danach sucht.
 */
function PrivateColumn({
  currentTicket,
  customerObjectKey,
  agendaItem,
  onAgendaChange,
  upNext,
  detailError,
  summary,
  onSummarySaved,
  comments,
  releasedInternal,
  onToggleRelease,
  note,
  onNoteChange,
  shownAttachmentId,
  onShowAttachment,
  focusedCommentId,
  onFocusComment,
  isDesktop,
}: {
  currentTicket: CustomerSafeTicket | null;
  /** Kunden-Objektschlüssel — der Kommentar-Editor invalidiert damit die Ticketliste. */
  customerObjectKey: string;
  /** Der Agenda-Punkt der laufenden Folie — `null` außerhalb des Modus `agendaItem`. */
  agendaItem: AgendaItem | null;
  onAgendaChange: (
    kind: AgendaKind,
    patch: Partial<Pick<AgendaItem, "title" | "body" | "enabled">>,
  ) => void;
  /** Was nach dieser Folie kommt (Agenda-Punkt ohne Schlüssel, Ticket mit). */
  upNext: { badge: string | null; title: string } | null;
  /** Fehler von `useTicketDetail` fürs aktuelle Ticket — `null`, solange nichts schiefging. */
  detailError: unknown;
  summary: TicketSummaryDto | null;
  onSummarySaved: (dto: TicketSummaryDto) => void;
  /** ALLE Kommentare des Tickets, neueste zuerst (s. `comments` im Cockpit). */
  comments: CustomerSafeRichComment[];
  releasedInternal: string[];
  onToggleRelease: (commentId: string) => void;
  note: string;
  onNoteChange: (text: string) => void;
  shownAttachmentId: string | null;
  onShowAttachment: (a: { id: string; filename: string } | null) => void;
  /** Kommentar, der gerade groß auf der Bühne liegt (#272). */
  focusedCommentId: string | null;
  onFocusComment: (id: string | null) => void;
  isDesktop: boolean;
}) {
  const { t } = useTranslation();
  const edit = useEditSummary();
  const [draft, setDraft] = useState(summary?.text ?? "");
  /** Vergrößert betrachtetes Bild — nur für den Lead, nie auf der Bühne. */
  const [enlarged, setEnlarged] = useState<{ id: string; filename: string } | null>(null);

  const images = useMemo(
    () => (currentTicket?.attachments ?? []).filter((a) => a.mimeType.startsWith("image/")),
    [currentTicket],
  );
  /**
   * Welche Bilder hängen an welchem Kommentar? Jira bettet Screenshots als
   * `<img src="/api/v1/customer-view/attachments/<id>">` ein — dieselbe
   * Adresse, aus der `referencedAttachmentIds` die Ids zurückgewinnt. So steht
   * der Knopf „Zeigen" bei dem Kommentar, von dem im Termin gerade die Rede
   * ist, statt in einer langen Liste weiter unten.
   */
  const imagesByComment = useMemo(() => {
    const map = new Map<string, CustomerSafeAttachment[]>();
    for (const c of comments) {
      const ids = referencedAttachmentIds([c.bodyHtml]);
      const list = images.filter((a) => ids.has(a.id));
      if (list.length > 0) map.set(c.id, list);
    }
    return map;
  }, [comments, images]);
  /** Bilder, die in KEINEM Kommentar vorkommen — sie brauchen einen eigenen Platz. */
  const otherImages = useMemo(() => {
    const referenced = referencedAttachmentIds(comments.map((c) => c.bodyHtml));
    return images.filter((a) => !referenced.has(a.id));
  }, [comments, images]);

  // Markdown ist nicht billig, und der Punkt ändert sich nur beim Tippen —
  // ohne `useMemo` liefe der Renderer bei jedem Zustandswechsel des Cockpits
  // (jede Folie, jeder Schalter) erneut über denselben Text.
  const agendaPreviewHtml = useMemo(
    () => (agendaItem ? renderAgendaMarkdown(agendaItem.body) : ""),
    [agendaItem],
  );

  const save = () => {
    if (!currentTicket) return;
    edit.mutate(
      { key: currentTicket.key, text: draft.trim(), issueUpdatedAt: currentTicket.updatedIso },
      {
        onSuccess: (dto) => {
          onSummarySaved(dto);
          setDraft(dto.text);
          notifications.show({ color: "green", message: t("pages.customerView.present.saved", { key: currentTicket.key }) });
        },
        onError: () => {
          notifications.show({ color: "red", message: t("pages.customerView.present.saveFailed", { key: currentTicket.key }) });
        },
      },
    );
  };

  /** Die drei Knöpfe zu einem Bild: zeigen/ausblenden und vergrößern. */
  const imageButtons = (a: CustomerSafeAttachment) => {
    const shown = shownAttachmentId === a.id;
    return (
      <Group key={a.id} gap={4} wrap="nowrap">
        <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>{a.filename}</Text>
        <Button
          size="compact-xs"
          variant={shown ? "filled" : "default"}
          onClick={() => onShowAttachment(shown ? null : { id: a.id, filename: a.filename })}
        >
          {shown ? t("pages.customerView.present.hideImage") : t("pages.customerView.present.showImage")}
        </Button>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() => setEnlarged({ id: a.id, filename: a.filename })}
        >
          {t("pages.customerView.present.enlarge")}
        </Button>
      </Group>
    );
  };

  return (
    <Paper withBorder p="xs" h="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
        {t("pages.customerView.present.private")}
      </Text>
      {/* Begründung für die bedingte feste Höhe s. AgendaColumn weiter oben. */}
      <ScrollArea h={isDesktop ? undefined : "calc(100vh - 420px)"} style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="sm" pr="xs">
          {detailError !== null && (
            isJiraReauthRequired(detailError) ? (
              <ReconnectBanner />
            ) : (
              <Alert color="red" variant="light">{t("pages.customerView.present.detailFailed")}</Alert>
            )
          )}

          {!currentTicket && !agendaItem && (
            <Text size="sm" c="dimmed">{t("pages.customerView.present.noCurrentTicket")}</Text>
          )}

          {agendaItem && (
            <Stack gap={4}>
              <Text size="xs" fw={600}>{t("pages.customerView.present.agendaEdit")}</Text>
              <TextInput
                size="xs"
                maxLength={AGENDA_TITLE_MAX}
                value={agendaItem.title}
                aria-label={t("pages.customerView.present.agendaTitle")}
                onChange={(e) => onAgendaChange(agendaItem.kind, { title: e.currentTarget.value })}
                // Beim Tippen darf das Feld leer sein (sonst ließe sich der
                // Titel nie ersetzen) — beim Verlassen kehrt der Vorgabetitel
                // zurück, statt dass eine leere Überschrift auf die Bühne geht.
                onBlur={() => {
                  if (agendaItem.title.trim() === "") {
                    onAgendaChange(agendaItem.kind, { title: agendaTitleFallback(agendaItem.kind, t) });
                  }
                }}
              />
              <Textarea
                autosize
                minRows={5}
                maxRows={14}
                maxLength={AGENDA_ITEM_BODY_MAX}
                value={agendaItem.body}
                aria-label={t("pages.customerView.present.agendaBody")}
                styles={{ input: { fontFamily: "monospace", fontSize: "0.8rem" } }}
                onChange={(e) => onAgendaChange(agendaItem.kind, { body: e.currentTarget.value })}
              />
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt={4}>
                {t("pages.customerView.present.agendaPreview")}
              </Text>
              {/*
                Genau das HTML, das über den Kanal geht — `renderAgendaMarkdown`
                sanitisiert bereits, die Bühne führt es ein zweites Mal durch
                denselben Riegel (Sicherheitsinvariante, Spec D9). Die Vorschau
                ist KEINE Freigabestufe: Jede Änderung oben steht sofort auf
                der Bühne. Sie ist die Gegenprobe zum Markdown daneben — was
                steht da eigentlich, wenn es gesetzt ist.
              */}
              <div
                className="agenda-preview"
                dangerouslySetInnerHTML={{ __html: agendaPreviewHtml }}
              />
            </Stack>
          )}

          {currentTicket && (
            <Stack gap={4}>
              <Group gap="xs">
                <Text size="xs" fw={600}>{t("pages.customerView.present.summaryLabel")}</Text>
                {summary?.fallback && <Badge size="xs" color="yellow" variant="light">{t("pages.customerView.present.fallbackBadge")}</Badge>}
                {summary?.edited && <Badge size="xs" color="green" variant="light">{t("pages.customerView.present.editedBadge")}</Badge>}
              </Group>
              <Textarea
                autosize
                minRows={4}
                maxRows={8}
                maxLength={SUMMARY_MAX_LENGTH}
                value={draft}
                aria-label={t("pages.customerView.present.summaryLabel")}
                onChange={(e) => setDraft(e.currentTarget.value)}
              />
              <Group gap="xs">
                <Button
                  size="xs"
                  loading={edit.isPending}
                  disabled={draft.trim() === "" || draft === (summary?.text ?? "")}
                  onClick={save}
                >
                  {t("pages.customerView.present.summaryApply")}
                </Button>
                <Text size="xs" c="dimmed">
                  {t("pages.customerView.present.characters", { n: draft.length, max: SUMMARY_MAX_LENGTH })}
                </Text>
              </Group>
            </Stack>
          )}

          {currentTicket && (
            <Stack gap={4}>
              <Text size="xs" fw={600}>
                {t("pages.customerView.present.commentsHeading", { n: comments.length })}
              </Text>
              {comments.length === 0 && (
                <Text size="xs" c="dimmed">{t("pages.customerView.present.noComments")}</Text>
              )}
              {comments.map((c) => {
                const released = releasedInternal.includes(c.id);
                const attached = imagesByComment.get(c.id) ?? [];
                // Fokussieren lässt sich nur, was ohnehin auf der Bühne steht;
                // `buildStageSnapshot` prüft dasselbe noch einmal.
                const onStage = c.isPublic || released;
                const focused = focusedCommentId === c.id;
                return (
                  <Paper
                    key={c.id}
                    withBorder
                    p={6}
                    bg={released ? "var(--mantine-color-yellow-light)" : undefined}
                    style={focused ? { borderColor: "var(--mantine-color-blue-6)", borderWidth: 2 } : undefined}
                  >
                    <Group justify="space-between" gap="xs" wrap="nowrap" mb={4}>
                      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                        <Badge size="xs" variant="light" color={c.isPublic ? "blue" : "yellow"}>
                          {c.isPublic
                            ? t("pages.customerView.present.commentPublic")
                            : t("pages.customerView.present.commentInternal")}
                        </Badge>
                        <Text size="xs" c="dimmed" lineClamp={1}>{c.authorName}</Text>
                        {/* Datum wie auf der Bühne, damit Lead und Kunde über
                            denselben Kommentar reden (#274); die Uhrzeit
                            steht im Tooltip. */}
                        <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }} title={absoluteDate(c.createdIso)}>
                          {formatBerlinDate(c.createdIso)}
                        </Text>
                      </Group>
                      {/* Freigeben lässt sich nur, was der Kunde noch nicht
                          sieht — ein öffentlicher Kommentar steht ohnehin auf
                          der Bühne. */}
                      {!c.isPublic && (
                        <Switch
                          size="xs"
                          checked={released}
                          onChange={() => onToggleRelease(c.id)}
                          label={t("pages.customerView.present.showOnStage")}
                          description={t("pages.customerView.present.showOnStageHint")}
                        />
                      )}
                    </Group>
                    {/* Der volle Verlauf inklusive eingebetteter Screenshots —
                        auf Daumennagelgröße begrenzt (`.cv-comment` in
                        global.css), sonst füllt ein einziger Screenshot die
                        Spalte. */}
                    <div className="cv-comment">
                      <RichHtml html={c.bodyHtml} />
                    </div>
                    {onStage && (
                      <Button
                        size="compact-xs"
                        variant={focused ? "filled" : "light"}
                        mt={4}
                        title={t("pages.customerView.present.focusCommentHint")}
                        onClick={() => onFocusComment(focused ? null : c.id)}
                      >
                        {focused
                          ? t("pages.customerView.present.unfocusComment")
                          : t("pages.customerView.present.focusComment")}
                      </Button>
                    )}
                    {attached.length > 0 && (
                      <Stack gap={2} mt={4}>{attached.map(imageButtons)}</Stack>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}

          {currentTicket && (
            <Stack gap={4}>
              <Text size="xs" fw={600}>{t("pages.customerView.present.notesLabel")}</Text>
              <Textarea
                autosize
                minRows={3}
                maxRows={8}
                value={note}
                aria-label={t("pages.customerView.present.notesLabel")}
                placeholder={t("pages.customerView.present.notesPlaceholder")}
                onChange={(e) => onNoteChange(e.currentTarget.value)}
              />
            </Stack>
          )}

          {/* Die Lösungscheckliste muss vor „Erledigt" abgeschickt sein —
              ohne das Panel hier sperrte das Statusmenü im Termin, und es
              gab keinen Weg, die Sperre zu lösen (#277). Diese Spalte
              erreicht die Bühne nie; Formularinhalte bleiben beim Lead. */}
          {currentTicket && (
            <ChecklistPanel key={currentTicket.key} ticketKey={currentTicket.key} objectKey={customerObjectKey} />
          )}

          {currentTicket && otherImages.length > 0 && (
            <Stack gap={4}>
              <Text size="xs" fw={600}>{t("pages.customerView.present.otherImages", { n: otherImages.length })}</Text>
              {otherImages.map(imageButtons)}
            </Stack>
          )}

          {upNext && (
            <Paper withBorder p={6}>
              <Text size="xs" c="dimmed" tt="uppercase">{t("pages.customerView.present.upNext")}</Text>
              <Group gap={6} wrap="nowrap">
                {upNext.badge !== null && (
                  <Text size="xs" c="dimmed" ff="monospace">{upNext.badge}</Text>
                )}
                <Text size="sm" lineClamp={2}>{upNext.title}</Text>
              </Group>
            </Paper>
          )}

          {/*
            Das Kommentarfeld klebt am unteren Rand der Spalte — dieselbe
            Lösung wie in der Ticketansicht (`TicketDetail.tsx`, #235) und aus
            demselben Grund: Im Termin will der Lead antworten, ohne vorher an
            Verlauf, Notizen und Bildern vorbeizuscrollen.

            Zwei Bedingungen, beide notwendig: DIREKTES Kind dieses `Stack`
            (`position: sticky` klebt nur im eigenen Elternelement) und ZULETZT
            im Fluss — `bottom: 0` schiebt ein Element nur nach unten, nie nach
            oben. Deshalb stehen Notizen und Bilder jetzt DARÜBER. Zwischen
            hier und dem Viewport der ScrollArea liegt kein `overflow: hidden`:
            der `Stack` trägt nur `pr`, die ScrollArea scrollt selbst, und das
            `Paper` darum ist ein Flex-Container ohne eigenen Überlauf.

            `useAddComment` setzt den geschriebenen Kommentar sofort in den
            Detail-Cache: Ein kundensichtbarer steht damit im nächsten
            Snapshot auf der Bühne, ein interner oben im Verlauf mit seinem
            Freigabeschalter. Die Sicherheitsinvariante bleibt unberührt — auf
            die Bühne kommt er über `buildStageSnapshot`, weil er öffentlich
            IST, nicht weil das Cockpit etwas Zusätzliches schickt.
          */}
          {currentTicket && (
            <Box
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                background: "var(--mantine-color-body)",
                borderTop: "1px solid var(--msc-linie)",
                paddingTop: "var(--mantine-spacing-xs)",
              }}
            >
              <CommentComposer ticketKey={currentTicket.key} objectKey={customerObjectKey} />
            </Box>
          )}
        </Stack>
      </ScrollArea>

      {/* Vergrößern ist reine Lead-Ansicht: Was der Kunde sieht, entscheidet
          allein „Zeigen" (der Bühnen-Snapshot), nicht dieser Dialog. */}
      <Modal
        opened={enlarged !== null}
        onClose={() => setEnlarged(null)}
        size="xl"
        centered
        title={enlarged?.filename ?? ""}
      >
        {enlarged && <Image src={attachmentUrl(enlarged.id)} alt={enlarged.filename} fit="contain" mah="70vh" />}
      </Modal>
    </Paper>
  );
}
